import React from 'react';
import PropTypes from 'prop-types';
import {
  FormattedMessage,
  defineMessages,
} from 'react-intl';
import st from 'core/shared-translations';
import environment from 'lib/environment';
import ProductLink from './product-link-view';
import appconfig from 'config/appconfig';
import {
  PersonOutlinedIcon,
  HelpCircleOutlinedIcon,
  ListOutlinedIcon,
  FileTextOutlinedIcon,
  IdCardOutlinedIcon,
  LockedFilledIcon,
  CodeOutlinedIcon,
  ExternalLinkOutlinedIcon,
  PeopleOutlinedIcon,
  CashOutlinedIcon,
  SignOutOutlinedIcon,
  GotoBlackYellowIcon,
  ReaderOutlinedIcon
} from '@getgo/chameleon-icons/react';
import {
  MenuItem,
  IconButton,
  Menu,
  MenuSeparator,
  PopoverV2,
  Typography,
  Avatar} from '@getgo/chameleon-web-react-wrapper';
import classes from './main-nav-view.scss';
import { getLocalizedUrl } from 'lib/utility-functions';
import { Box } from '@mui/material';

const myAccountUrl = environment.get().myAccount_url;
const adminCenterUrl = environment.get().admin_url;
const billingUrl = environment.get().billing_url;
const apiReferenceUrl = environment.get().api_reference_url;
const personalInfoUrl = environment.get().personal_info_url;
const singInSecurityUrl = environment.get().signin_security_url;
const developerTools = environment.get().developer_tools_url;
const cgpUrl = environment.get().cgp_url;
const supportUrl = `${apiReferenceUrl}${appconfig.infoURLs.supportUrl}`;
const faqUrl = `${apiReferenceUrl}${appconfig.infoURLs.faqUrl}`;
const privacyPolicyUrl = environment.get().privacy_policy_url;
const aboutUsUrl = environment.get().about_us_url;


const t = defineMessages({
  'nav.hamburgertoggle': {
    id: 'nav.hamburgertoggle',
    defaultMessage: 'Toggle navigation',
    description: 'The nav hamburger toggle which shows and hides nav menu for mobile devices',
  },
  'nav.navbar.clients': {
    id: 'nav.navbar.clients',
    defaultMessage: 'OAuth Clients',
    description: 'The nav bar Clients link',
  },
  'nav.navbar.apidocumentation': {
    id: 'nav.navbar.apidocumentation',
    defaultMessage: 'Product APIs',
    description: 'The nav bar Product APIs link',
  },
  'nav.navbar.personal.info': {
    id: 'nav.navbar.personal.info',
    defaultMessage: 'Personal Info',
    description: 'The nav bar Personal Info link',
  },
  'nav.navbar.signin.security': {
    id: 'nav.navbar.signin.security',
    defaultMessage: 'Sign In & Security',
    description: 'The nav bar Sign In & Security link',
  },
  'nav.navbar.developer.tools': {
    id: 'nav.navbar.developer.tools',
    defaultMessage: 'Developer Tools',
    description: 'The nav bar Developer Tools link',
  },
  'nav.navbar.help': {
    id: 'nav.navbar.help',
    defaultMessage: 'Help',
    description: 'Help icon label',
  },
  'nav.navbar.faq': {
    id: 'nav.navbar.faq',
    defaultMessage: 'FAQ',
    description: 'FAQ menu label',
  },
  'nav.navbar.new-features': {
    id: 'nav.navbar.new-features',
    defaultMessage: 'New Features',
    description: 'New Features menu label',
  },
  'nav.navbar.sign-out': {
    id: 'nav.navbar.sign-out',
    defaultMessage: 'Sign Out',
    description: 'Sign Out menu label',
  },
  'nav.navbar.my-account': {
    id: 'nav.navbar.my-account',
    defaultMessage: 'My Account',
    description: 'My Account menu label',
  },
  'nav.navbar.admin-center': {
    id: 'nav.navbar.admin-center',
    defaultMessage: 'Admin Center',
    description: 'Admin Center menu label',
  },
  'nav.navbar.billing': {
    id: 'nav.navbar.billing',
    defaultMessage: 'Billing',
    description: 'Billing menu label',
  },
  'nav.lmi.account': {
    id: 'nav.lmi.account',
    defaultMessage: 'GoTo Account: {name}',
    description: 'Hover text to be displayed over the Nav Bar\'s small avatar image',
  },
  'footer.aboutus': {
    id: 'footer.aboutus',
    defaultMessage: 'About us',
    description: 'The about us link',
  },
  'footer.legalinfo': {
    id: 'footer.legalinfo',
    defaultMessage: 'Legal info',
    description: 'The terms of service link',
  },
  'footer.copyright': {
    id: 'footer.copyright',
    defaultMessage: '© {currentYear} GoTo Group, Inc. All Rights Reserved',
    description: 'The copyright text',
  },
});

const currentYear = new Date().getFullYear();

export const MainNav = ({
  logout,
  displayName,
  meIsAccountAdmin,
  myProducts,
  avatarImageUrl,
  meIsBillingAdmin,
  displayEmail,
  showSupportLink,
  gotoAppEnabled,
  language,
  isInternalDev = false
}) => {
  return (
    <Menu label="main-nav-bar" className={classes['navContainer']} role="menu" aria-label="navbar">
      <Box role="menuitem" className={classes['navContainerTitle']}>
        <GotoBlackYellowIcon size="50px" />
        <Typography size="20px" variant="body-large">
          <FormattedMessage {...st['shared.navbar.app-title']} />
        </Typography>
      </Box>
      <Box role="menuitem" className={classes['iconGroup']}>
        {showSupportLink &&
          <Box className={classes["supportLinks"]}>
            <IconButton id="help-trigger" labelId={<FormattedMessage {...t['nav.navbar.help']} />}>
              <HelpCircleOutlinedIcon size="24px" />
            </IconButton>
            <PopoverV2
              triggerId="help-trigger"
              position="bottom-end"
              menu
              minWidth={238}
            >
              <a href={faqUrl}>
                <MenuItem leadingIcon={<ListOutlinedIcon color="type-color-default" />}>
                  <FormattedMessage {...t['nav.navbar.faq']} />
                </MenuItem>
              </a>
              <a href={supportUrl}>
                <MenuItem leadingIcon={<FileTextOutlinedIcon color="type-color-default" />}>
                  <FormattedMessage {...st['shared.help.support']} />
                </MenuItem>
              </a>
              <MenuSeparator />
              <a href={getLocalizedUrl(aboutUsUrl, language)}>
                <MenuItem trailingIcon={<ExternalLinkOutlinedIcon color="type-color-default" />}>
                  <FormattedMessage {...t['footer.aboutus']} />
                </MenuItem>
              </a>
              <a href={getLocalizedUrl(privacyPolicyUrl, language)}>
                <MenuItem trailingIcon={<ExternalLinkOutlinedIcon color="type-color-default" />}>
                  <FormattedMessage {...t['footer.legalinfo']} />
                </MenuItem>
              </a>
            </PopoverV2>
          </Box>}
        <Box>
          <IconButton
            labelId={<FormattedMessage {...t['nav.hamburgertoggle']} />}
            id="avatar-trigger"
            >
            <Avatar size="small" variant="secondary">
              <img
                alt="userAvatar"
                src={avatarImageUrl}
                title={displayName}
              />
            </Avatar>
          </IconButton>
          <PopoverV2
            menu
            position="bottom-end"
            triggerId="avatar-trigger"
            label="avatar options"
            width={window.innerWidth < 762 && window.innerWidth - 29}
            >
            <Box className={classes["avatarInfo"]}>
              <IconButton id="user-option-trigger" role="button">
                <img src={avatarImageUrl} alt="displayEmail" className={classes["navIcon"]} />
              </IconButton>
              <Box>
                <Typography variant="body-small" title={displayName} color="type-color-default">{displayName}</Typography>
                <Typography title={displayEmail} variant="caption-medium-strong" color="type-color-secondary">{displayEmail}</Typography>
              </Box>
            </Box>
            <MenuSeparator />
            <a href={getLocalizedUrl(myAccountUrl, language)}>
              <MenuItem leadingIcon={<PersonOutlinedIcon color="type-color-default" />}>
                <FormattedMessage {...t['nav.navbar.my-account']} />
              </MenuItem>
            </a>
            <a href={appconfig.routes.clients()}>
              <MenuItem leadingIcon={<CodeOutlinedIcon color="type-color-default" />}>
                <FormattedMessage {...t['nav.navbar.clients']} />
              </MenuItem>
            </a>
            <a href={apiReferenceUrl}>
              <MenuItem leadingIcon={<ReaderOutlinedIcon color="type-color-default" />}>
                <FormattedMessage {...t['nav.navbar.apidocumentation']} />
              </MenuItem>
            </a>
            <a href={personalInfoUrl}>
              <MenuItem leadingIcon={<IdCardOutlinedIcon color="type-color-default" />}>
                <FormattedMessage {...t['nav.navbar.personal.info']} />
              </MenuItem>
            </a>
            <a href={singInSecurityUrl}>
              <MenuItem leadingIcon={<LockedFilledIcon color="type-color-default" />}>
                <FormattedMessage {...t['nav.navbar.signin.security']} />
              </MenuItem>
            </a>
            <a href={developerTools}>
              <MenuItem leadingIcon={<CodeOutlinedIcon color="type-color-default" />}>
                <FormattedMessage {...t['nav.navbar.developer.tools']} />
              </MenuItem>
            </a>
            {isInternalDev && cgpUrl &&
              <a href={cgpUrl}>
                <MenuItem leadingIcon={<CodeOutlinedIcon color="type-color-default" />}>
                  <FormattedMessage {...st['shared.cgp.link.label']} />
                </MenuItem>
              </a>}
            {meIsAccountAdmin &&
              <a href={getLocalizedUrl(adminCenterUrl, language)}>
                <MenuItem leadingIcon={<PeopleOutlinedIcon color="type-color-default" />}>
                  <FormattedMessage {...t['nav.navbar.admin-center']} />
                </MenuItem>
              </a>}
            {meIsBillingAdmin &&
              <a href={getLocalizedUrl(billingUrl, language)}>
                <MenuItem leadingIcon={<CashOutlinedIcon color="type-color-default" />}>
                  <FormattedMessage {...t['nav.navbar.billing']} />
                </MenuItem>
              </a>}
            {!!myProducts.length && <MenuSeparator />}
            {!!myProducts.length &&
                myProducts.map(product =>
                  <ProductLink
                    key={product}
                    product={product}
                    gotoAppEnabled={gotoAppEnabled} />)}
            <MenuSeparator />
            <a href="#" onClick={logout}>
              <MenuItem leadingIcon={<SignOutOutlinedIcon color="type-color-default" />}>
                <FormattedMessage {...t['nav.navbar.sign-out']} />
              </MenuItem>
            </a>
            <MenuSeparator />
            <Box className={classes['copyRightBlock']}>
              <Typography variant="caption-small" color="type-color-secondary">
                <FormattedMessage {...t['footer.copyright']} values={{ currentYear }} />
              </Typography>
            </Box>
          </PopoverV2>
        </Box>
      </Box>
    </Menu>
  );
};

MainNav.propTypes = {
  logout: PropTypes.func.isRequired,
  displayName: PropTypes.string.isRequired,
  meIsAccountAdmin: PropTypes.bool.isRequired,
  myProducts: PropTypes.array.isRequired,
  avatarImageUrl: PropTypes.string.isRequired,
  meIsBillingAdmin: PropTypes.bool.isRequired,
  displayEmail: PropTypes.string.isRequired,
  showSupportLink: PropTypes.bool.isRequired,
  gotoAppEnabled: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  isInternalDev: PropTypes.bool,
};

export default MainNav;
